import type { Transaction } from '@familywallet/shared';
import { useMutation } from '@tanstack/react-query';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';
import { FormError } from '../components/FormError';
import { PageHeader } from '../components/PageHeader';
import { exchangeReceived } from '../components/TransactionRow';
import { Button, buttonVariants } from '../components/ui/button';
import { Card, ListGroup, SectionLabel } from '../components/ui/card';
import { api } from '../lib/api';
import { useCan, useMe } from '../lib/auth';
import { formatDay, todayIn } from '../lib/dates';
import { useDebt, useTransaction } from '../lib/ledger';
import { useInvalidateLedger } from '../lib/ledger-mutations';
import { useIdempotencyKey } from '../lib/members';
import { isSplitPayment } from '../lib/fx-forms';
import { formatAmount, formatRateValue } from '../lib/money';
import type { DebtPrefill } from './NewDebtPage';

/** Where "Record the right one" goes after a correction, with what can be filled in again. */
function useRecordAgain(tx: Transaction): { to: string; state?: unknown } {
  const debt = useDebt(tx.debt?.debtId ?? '');
  const d = tx.debt && debt.data ? debt.data : null;
  if (tx.kind === 'exchange') return { to: '/add?kind=exchange' };
  if (isSplitPayment(tx)) return { to: `/add?kind=${tx.kind}&foreign=1` };
  if (!tx.debt) return { to: '/add' };
  if (tx.kind === 'repay' || tx.kind === 'collect') {
    const debtLine = tx.lines.find((l) => l.role === 'debt' && l.amount !== null);
    const otherCurrency = debtLine && debtLine.currency !== tx.currency;
    if (otherCurrency) {
      return {
        to: `/debts/${tx.debt.debtId}/pay`,
        state: {
          prefill: {
            amount: debtLine.amount!.replace('-', ''),
            paidCurrency: tx.currency,
            occurredOn: tx.occurredOn,
            note: tx.description,
          },
        },
      };
    }
    return {
      to: `/debts/${tx.debt.debtId}/pay`,
      state: {
        prefill: {
          ...(tx.amount ? { amount: tx.amount } : {}),
          occurredOn: tx.occurredOn,
          note: tx.description,
        },
      },
    };
  }
  const prefill: DebtPrefill = {
    direction:
      tx.debt.label === 'borrowed' || tx.debt.label === 'existing_owed' ? 'borrowed' : 'lent',
    existing: tx.kind === 'debt_opening',
    currency: tx.currency,
    ...(tx.amount ? { amount: tx.amount } : {}),
    occurredOn: tx.occurredOn,
    note: tx.description,
    personLabel: tx.debt.personName,
    ...(d
      ? {
          ownerMemberId: d.owner.memberId,
          ...(d.person.type === 'outside'
            ? { personId: d.person.personId }
            : { memberId: d.person.memberId }),
        }
      : {}),
  };
  return { to: '/debts/new', state: { prefill } };
}

/** One transaction: its lines, rates, and Correct (a reversal, then record the right one). */
export function TransactionDetailPage() {
  const { id = '' } = useParams();
  const { t } = useTranslation();
  const me = useMe();
  const tx = useTransaction(id);
  if (!me.data || tx.isPending) return <p role="status">{t('app.loading')}</p>;
  if (tx.isError) {
    return (
      <div className="flex flex-col gap-4">
        <PageHeader title={t('txDetail.title')} back="/activity" />
        <Card role="alert" className="flex flex-col gap-3">
          <FormError error={tx.error} />
          <Button variant="secondary" onClick={() => void tx.refetch()}>
            {t('common.retry')}
          </Button>
        </Card>
      </div>
    );
  }
  return (
    <Detail
      tx={tx.data}
      baseCurrency={me.data.family.baseCurrency}
      timezone={me.data.family.timezone}
      myId={me.data.member.id}
    />
  );
}

function Detail({
  tx,
  baseCurrency,
  timezone,
  myId,
}: {
  tx: Transaction;
  baseCurrency: string;
  timezone: string;
  myId: string;
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const invalidate = useInvalidateLedger();
  const reverseOwn = useCan('transactions.reverse_own');
  const reverseAny = useCan('transactions.reverse_any');
  const [idempotencyKey, renewKey] = useIdempotencyKey();
  const [confirming, setConfirming] = useState(false);
  const [justReversed, setJustReversed] = useState(false);
  const recordAgain = useRecordAgain(tx);

  const reverse = useMutation({
    mutationFn: () => api.transactions.reverse(tx.id, {}, { idempotencyKey }),
    onSuccess: async () => {
      renewKey();
      setConfirming(false);
      setJustReversed(true);
      await invalidate();
    },
  });

  const canCorrect =
    tx.kind !== 'reversal' &&
    tx.reversedByTransactionId === null &&
    !tx.lines.some((l) => l.masked) &&
    (reverseAny || (reverseOwn && tx.createdBy.memberId === myId));
  const title =
    tx.debt?.personName || tx.payee?.name || tx.description || t(`activity.kinds.${tx.kind}`);
  const kindLabel = tx.debt ? t(`debts.labels.${tx.debt.label}`) : t(`activity.kinds.${tx.kind}`);
  const rates = Object.entries(tx.rates).filter(([currency]) => currency !== baseCurrency);
  const received = tx.kind === 'exchange' ? exchangeReceived(tx) : null;
  // In two currencies, the rate used is the deal's (or the shop's) unless it was estimated.
  const dealLabel =
    tx.kind === 'exchange'
      ? t('txDetail.dealRate')
      : Object.keys(tx.rates).length > 1 && !tx.estimatedWithFamilyRate
        ? t('txDetail.shopRate')
        : t('txDetail.rate');
  const today = todayIn(timezone);

  return (
    <div className="flex flex-col gap-4">
      <PageHeader title={title} back="/activity" />
      {tx.amount !== null && (
        <section className="flex flex-col gap-1 rounded-3xl bg-hero p-5 text-hero-fg">
          <span className="text-[13px] text-hero-muted">{kindLabel}</span>
          <span
            dir="ltr"
            className="text-[38px] leading-tight font-semibold tracking-[-0.5px] tabular-nums"
          >
            {formatAmount(tx.amount, tx.currency, i18n.language)}
          </span>
          {received ? (
            <span dir="ltr" className="text-[15px] text-hero-muted tabular-nums">
              → {formatAmount(received.amount, received.currency, i18n.language)}
            </span>
          ) : (
            tx.currency !== baseCurrency &&
            tx.baseAmount !== null && (
              <span dir="ltr" className="text-[13px] text-hero-muted tabular-nums">
                ≈ {formatAmount(tx.baseAmount, baseCurrency, i18n.language)}
              </span>
            )
          )}
          {tx.estimatedWithFamilyRate && (
            <span className="text-[13px] text-hero-muted">{t('foreign.estimated')}</span>
          )}
        </section>
      )}

      {justReversed && (
        <Card role="status" className="flex flex-col gap-3">
          <p>{t('txDetail.reversed')}</p>
          <Link to={recordAgain.to} state={recordAgain.state} className={buttonVariants()}>
            {t('txDetail.recordAgain')}
          </Link>
        </Card>
      )}
      {tx.reversesTransactionId && (
        <Card className="flex flex-col gap-2">
          <p className="text-sm">{t('txDetail.correctionOf')}</p>
          <Link
            to={`/activity/${tx.reversesTransactionId}`}
            className={buttonVariants({ variant: 'secondary' })}
          >
            {t('txDetail.seeOriginal')}
          </Link>
        </Card>
      )}
      {tx.reversedByTransactionId && !justReversed && (
        <Card className="flex flex-col gap-2">
          <p className="text-sm">{t('txDetail.corrected')}</p>
          <Link
            to={`/activity/${tx.reversedByTransactionId}`}
            className={buttonVariants({ variant: 'secondary' })}
          >
            {t('txDetail.seeCorrection')}
          </Link>
        </Card>
      )}

      <ListGroup>
        <InfoRow label={t('txDetail.date')}>
          {formatDay(tx.occurredOn, today, i18n.language, {
            today: t('activity.today'),
            yesterday: t('activity.yesterday'),
          })}
        </InfoRow>
        <InfoRow label={t('txDetail.kind')}>{kindLabel}</InfoRow>
        <InfoRow label={t('txDetail.recordedBy')}>
          {tx.recurring?.auto ? t('recurring.recordedAutomatically') : tx.createdBy.displayName}
        </InfoRow>
        {tx.recurring && (
          <InfoRow label={t('recurring.title')}>
            {t('recurring.fromRecurring', { name: tx.recurring.name })}
          </InfoRow>
        )}
        {tx.credit && (
          <>
            <InfoRow label={t(tx.kind === 'income' ? 'credit.receivedNow' : 'credit.paidNow')}>
              <span dir="ltr" className="tabular-nums">
                {tx.credit.paidNow === null
                  ? '•••'
                  : formatAmount(tx.credit.paidNow, tx.currency, i18n.language)}
              </span>
            </InfoRow>
            <InfoRow label={t('credit.onCredit')}>
              <span dir="ltr" className="tabular-nums">
                {formatAmount(tx.credit.onCredit, tx.currency, i18n.language)}
              </span>
            </InfoRow>
            {tx.credit.dueOn && (
              <InfoRow label={t('credit.dueOn')}>
                {formatDay(tx.credit.dueOn, today, i18n.language, {
                  today: t('activity.today'),
                  yesterday: t('activity.yesterday'),
                })}
              </InfoRow>
            )}
          </>
        )}
        {tx.debt?.interest && (
          <InfoRow label={t('txDetail.interest')}>
            <span dir="ltr" className="tabular-nums">
              {formatAmount(tx.debt.interest, tx.currency, i18n.language)}
            </span>
          </InfoRow>
        )}
        {rates.map(([currency, rate]) => (
          <InfoRow key={currency} label={dealLabel}>
            <span dir="ltr" className="tabular-nums">
              1 {baseCurrency} = {formatRateValue(rate, i18n.language)} {currency}
            </span>
          </InfoRow>
        ))}
        {Object.entries(tx.familyRates ?? {})
          .filter(([currency, rate]) => tx.rates[currency] !== rate)
          .map(([currency, rate]) => (
            <InfoRow key={`family-${currency}`} label={t('txDetail.familyRate')}>
              <span dir="ltr" className="tabular-nums">
                1 {baseCurrency} = {formatRateValue(rate, i18n.language)} {currency}
              </span>
            </InfoRow>
          ))}
      </ListGroup>
      {tx.credit && (
        <Link
          to={`/debts/${tx.credit.debtId}`}
          className={buttonVariants({ variant: 'secondary' })}
        >
          {t(tx.kind === 'income' ? 'credit.owedBy' : 'credit.owedTo')}: {tx.credit.personName}
        </Link>
      )}
      {tx.debt && (
        <Link to={`/debts/${tx.debt.debtId}`} className={buttonVariants({ variant: 'secondary' })}>
          {t('debts.title')}: {tx.debt.personName}
        </Link>
      )}

      <section className="flex flex-col gap-2">
        <SectionLabel>{t('txDetail.lines')}</SectionLabel>
        <ListGroup>
          {tx.lines.map((l, i) => (
            <li key={i} className="flex min-h-14 items-center justify-between gap-3 px-4 py-2">
              <span className="flex min-w-0 flex-col">
                <span className="truncate text-[15px]">
                  {l.masked || !l.accountName ? t('txDetail.hiddenLine') : l.accountName}
                </span>
                {l.note && <span className="truncate text-xs text-muted">{l.note}</span>}
              </span>
              <span dir="ltr" className="shrink-0 text-[15px] font-medium tabular-nums">
                {l.amount === null ? '•••' : formatAmount(l.amount, l.currency, i18n.language)}
              </span>
            </li>
          ))}
        </ListGroup>
      </section>

      {canCorrect && !justReversed && (
        <section className="flex flex-col gap-2">
          <p className="px-1 text-xs text-muted">{t('txDetail.correctHint')}</p>
          {confirming ? (
            <Card className="flex flex-col gap-3">
              <p className="font-medium">{t('txDetail.confirmCorrect')}</p>
              <p className="text-sm text-muted">{t('txDetail.confirmCorrectHint')}</p>
              <FormError error={reverse.error} />
              <Button
                variant="danger"
                disabled={reverse.isPending}
                onClick={() => reverse.mutate()}
              >
                {reverse.isPending ? t('common.saving') : t('txDetail.confirm')}
              </Button>
              <Button variant="ghost" onClick={() => setConfirming(false)}>
                {t('txDetail.cancel')}
              </Button>
            </Card>
          ) : tx.credit || isSplitPayment(tx) ? (
            <>
              <Link
                to={`/add?kind=${tx.kind}${isSplitPayment(tx) ? '&foreign=1' : ''}&correct=${tx.id}`}
                className={buttonVariants({ variant: 'secondary' })}
              >
                {t('txDetail.correct')}
              </Link>
              <Button variant="ghost" onClick={() => setConfirming(true)}>
                {t('credit.reverseOnly')}
              </Button>
            </>
          ) : (
            <Button variant="secondary" onClick={() => setConfirming(true)}>
              {t('txDetail.correct')}
            </Button>
          )}
        </section>
      )}
      {justReversed && (
        <Button variant="ghost" onClick={() => navigate('/activity')}>
          {t('common.back')}
        </Button>
      )}
    </div>
  );
}

function InfoRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <li className="flex min-h-12 items-center justify-between gap-3 px-4 py-2">
      <span className="text-[15px] text-muted">{label}</span>
      <span className="text-end text-[15px]">{children}</span>
    </li>
  );
}
